import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Firestore, doc, getDoc, updateDoc } from '@angular/fire/firestore';
import { InAppReview } from '@capacitor-community/in-app-review';
import { dbConst } from './dal/databaseConstants';

const GRACE_PERIOD_DAYS = 7;
const COOLDOWN_DAYS = 90;
const MAX_PROMPT_COUNT = 3;
const MIN_KEG_COUNT = 1;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class RatingService {
  private firestore = inject(Firestore);

  private _attemptedThisSession = false;

  /**
   * Run all gates and trigger the native rating dialog if eligible.
   * Safe to call multiple times — only the first call per session does anything.
   * Always exits silently on failure so it never disrupts the app.
   */
  async checkAndMaybePrompt(uid: string | null | undefined, kegCount: number): Promise<void> {
    if (this._attemptedThisSession) return;
    this._attemptedThisSession = true;

    try {
      if (!Capacitor.isNativePlatform()) return;
      if (!uid) return;
      if (kegCount < MIN_KEG_COUNT) return;

      const userDoc = doc(this.firestore, `${dbConst.users}/${uid}`);
      const snap = await getDoc(userDoc);
      if (!snap.exists()) return;
      const data = snap.data() as Record<string, unknown>;

      const firstSeenAt = await this.ensureFirstSeen(uid, data['firstSeenAt']);
      const now = Date.now();

      if (now - firstSeenAt.getTime() < GRACE_PERIOD_DAYS * MS_PER_DAY) {
        return;
      }

      const promptedAt = this.toDate(data['ratingPromptedAt']);
      if (promptedAt && now - promptedAt.getTime() < COOLDOWN_DAYS * MS_PER_DAY) {
        return;
      }

      const promptedCount = (data['ratingPromptCount'] as number) ?? 0;
      if (promptedCount >= MAX_PROMPT_COUNT) {
        return;
      }

      await InAppReview.requestReview();
      await this.recordPromptShown(uid, promptedCount);
    } catch (err) {
      console.error('[RatingService] failed silently', err);
    }
  }

  private async ensureFirstSeen(uid: string, raw: unknown): Promise<Date> {
    const existing = this.toDate(raw);
    if (existing) return existing;

    const now = new Date();
    const userDoc = doc(this.firestore, `${dbConst.users}/${uid}`);
    await updateDoc(userDoc, { firstSeenAt: now });
    return now;
  }

  private async recordPromptShown(uid: string, prevCount: number): Promise<void> {
    const userDoc = doc(this.firestore, `${dbConst.users}/${uid}`);
    await updateDoc(userDoc, {
      ratingPromptedAt: new Date(),
      ratingPromptCount: prevCount + 1,
    });
  }

  private toDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'object' && 'toDate' in (value as any) && typeof (value as any).toDate === 'function') {
      return (value as any).toDate();
    }
    return null;
  }
}
